import { IconFont } from '@byecode/ui'
import type { DataSourceAbstract, DTSelect, DTSelectItem, RelativeSelectConfig, SelectField, SingleSelectField } from '@lighthouse/core'
import { find, reduce } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'

import { useApplicationContext, useLanguageContext } from '../../../../../contexts'
import { useAppThemeContext } from '../../../../ApplicationContainer'
import { SelectDrawer } from '../../../../FieldDrawer'
import { applyFillPickerValue4CssColorValue } from '../../../../FillPicker'
import { SelectItemPreviewer } from '../../../SelectItemPreviewer'
import { SelectPlaceHolder } from '../../../SelectPlaceHolder'
import type { FieldBaseProps } from '../../../types'
import * as CM from '../../commonStyles'
import { CheckBoxGroup } from '../CheckBoxGroup'
import * as SC from '../styles'
import { TagGroup } from '../TagGroup'

interface SelectDrawerFieldProps extends FieldBaseProps {
    value: string[]
    selectConfig?: DTSelect
    relativeSelectConfig: RelativeSelectConfig
}

export const SelectDrawerField: React.FunctionComponent<SelectDrawerFieldProps> = props => {
    const {
        onChange,
        value: data,
        config,
        isControlled,
        readOnly,
        dataSource,
        selectConfig,
        relativeSelectConfig,
        onFetchDataSourceMeta,
        onRenderLabel
    } = props
    const { theme } = useAppThemeContext()
    const { fieldPointer = '', placeholder, relativeSelect, title } = relativeSelectConfig
    const { showMode, direction, align } = relativeSelect ?? {}
    const { id: pointer } = dataSource ?? {}
    const { breakPoint } = config
    const { options: selectOptions = [], multiple: canMultipleChoice } = selectConfig ?? {}
    const [value, setValue] = useState(data ?? [])
    const [newDataSource, setNewDataSource] = useState<DataSourceAbstract>()
    const [opened, setOpened] = useState(false)
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const usedValue = useMemo(() => (isControlled ? data : value), [data, isControlled, value])

    const fieldName = title ?? newDataSource?.schema?.[fieldPointer]?.name ?? ''

    const { pageTarget } = useApplicationContext()
    const { convertTextByLanguage } = useLanguageContext()

    const options = useMemo(() => {
        if (newDataSource) {
            const field = newDataSource.schema?.[fieldPointer]
            const options: DTSelectItem[] =
                field.type === 'singleSelect' ? (field as SingleSelectField).singleSelect.options : (field as SelectField).select.options
            return options.map(({ label, id, color }) => ({
                label,
                id,
                value: label,
                color,
                isDelete: !readOnly
            }))
        }
        return selectOptions.map(({ label, color, id }) => ({
            label,
            value: label,
            color,
            id,
            isDelete: !readOnly
        }))
    }, [fieldPointer, newDataSource, readOnly, selectOptions])

    const showValue = useMemo(() => {
        return reduce<string, DTSelectItem[]>(
            (preVal, label) => {
                const item = find(item => item.label === label, options)
                if (!item) {
                    return [
                        ...preVal,
                        {
                            label: convertTextByLanguage('optionNotFound'),
                            id: label,
                            color: 'crimson'
                        }
                    ]
                }
                return [...preVal, item]
            },
            [],
            usedValue
        )
    }, [convertTextByLanguage, options, usedValue])

    const handleMultiChange = useCallback(
        (value: string[]) => {
            setValue(value)
            onChange?.({ type: 'relativeSelect', value })
        },
        [onChange]
    )

    const handleOpen = useCallback(async () => {
        if (readOnly) {
            return
        }
        setOpened(true)
        if (!pointer) {
            return
        }
        const dataSource = await onFetchDataSourceMeta?.({ dsId: pointer })
        setNewDataSource(dataSource)
    }, [onFetchDataSourceMeta, pointer, readOnly])

    return useMemo(() => {
        switch (showMode) {
            case 'checkbox': {
                return (
                    <SC.Container>
                        <CheckBoxGroup
                            breakPoint={breakPoint}
                            options={options}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={usedValue}
                            disabled={readOnly}
                            onChange={handleMultiChange}
                            onRenderLabel={onRenderLabel}
                        />
                    </SC.Container>
                )
            }
            case 'tag': {
                return (
                    <SC.Container>
                        <TagGroup
                            options={options}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={usedValue}
                            disabled={readOnly}
                            onChange={handleMultiChange}
                        />
                    </SC.Container>
                )
            }
            default: {
                return (
                    <SC.Container data-field-input data-field-active={opened} data-field-disabled={!!readOnly}>
                        <SC.Wrapper onClick={handleOpen}>
                            <SC.TagWrapper>
                                {showValue.map(item => (
                                    <SelectItemPreviewer key={item.label} data={item} />
                                ))}
                                {showValue.length === 0 && <SelectPlaceHolder color={placeholderColor} label={placeholder} />}
                            </SC.TagWrapper>
                            <CM.IconWrapper data-field-icon>{!readOnly && <IconFont type="ArrowDownSmall" size={16} />}</CM.IconWrapper>
                        </SC.Wrapper>
                        <SelectDrawer
                            title={fieldName}
                            value={usedValue}
                            selectOptions={options}
                            isMultiple={canMultipleChoice}
                            onChange={handleMultiChange}
                            opened={opened}
                            onClose={() => setOpened(false)}
                            target={pageTarget}
                            tagComponent={({ data }) => (
                                <SelectItemPreviewer data={data} textStyles={{ flexShrink: 'unset', overflow: 'hidden' }} />
                            )}
                            style={{ fontFamily }}
                        />
                    </SC.Container>
                )
            }
        }
    }, [
        align,
        breakPoint,
        canMultipleChoice,
        direction,
        fieldName,
        fontFamily,
        handleMultiChange,
        handleOpen,
        onRenderLabel,
        opened,
        options,
        pageTarget,
        placeholder,
        placeholderColor,
        readOnly,
        showMode,
        showValue,
        usedValue
    ])
}
